import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle, XCircle, Loader2, CalendarDays, Users, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface BookingInfo {
  id: string;
  guestName: string | null;
  guestEmail: string;
  spots: number;
  status: string;
  alreadyCancelled: boolean;
  eventTitle: string;
  eventDate: string;
  producerName: string;
}

type PageState = 'loading' | 'found' | 'cancelled' | 'already_cancelled' | 'error' | 'invalid';

export default function BookingCancelPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [pageState, setPageState] = useState<PageState>('loading');
  const [booking, setBooking] = useState<BookingInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    if (!token || token.length < 10) {
      setPageState('invalid');
      return;
    }

    lookupBooking(token);
  }, [token]);

  async function lookupBooking(token: string) {
    try {
      const { data, error } = await supabase.functions.invoke('cancel-event-booking', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        body: null,
      });

      // GET requests with query params need a different approach
      // Use fetch directly with the function URL
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-event-booking?token=${encodeURIComponent(token)}`,
        {
          method: 'GET',
          headers: {
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 404) {
          setPageState('invalid');
          setErrorMessage('Bokningen kunde inte hittas.');
        } else {
          throw new Error(result.error || 'Ett fel uppstod');
        }
        return;
      }

      if (result.booking) {
        setBooking(result.booking);
        if (result.booking.alreadyCancelled) {
          setPageState('already_cancelled');
        } else {
          setPageState('found');
        }
      }
    } catch (error: any) {
      console.error('Error looking up booking:', error);
      setPageState('error');
      setErrorMessage(error.message || 'Ett oväntat fel uppstod.');
    }
  }

  async function handleCancelBooking() {
    if (!token) return;

    setIsCancelling(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-event-booking`,
        {
          method: 'POST',
          headers: {
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Avbokning misslyckades');
      }

      if (result.alreadyCancelled) {
        setPageState('already_cancelled');
        toast.info('Bokningen var redan avbokad');
      } else {
        setPageState('cancelled');
        toast.success('Din bokning har avbokats');
      }
    } catch (error: any) {
      console.error('Error cancelling booking:', error);
      toast.error(error.message || 'Kunde inte avboka. Försök igen.');
    } finally {
      setIsCancelling(false);
      setShowConfirmDialog(false);
    }
  }

  function formatDate(dateString: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('sv-SE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {pageState === 'loading' && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center gap-4 py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-muted-foreground">Hämtar bokningsinformation...</p>
              </div>
            </CardContent>
          </Card>
        )}

        {pageState === 'invalid' && (
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <XCircle className="h-6 w-6 text-destructive" />
              </div>
              <CardTitle>Ogiltig länk</CardTitle>
              <CardDescription>
                {errorMessage || 'Länken är ogiltig eller har gått ut. Kontrollera att du har kopierat hela länken från mailet.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  Gå till startsidan
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {pageState === 'error' && (
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <CardTitle>Något gick fel</CardTitle>
              <CardDescription>
                {errorMessage || 'Ett oväntat fel uppstod. Försök igen senare.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full" onClick={() => window.location.reload()}>
                Försök igen
              </Button>
            </CardContent>
          </Card>
        )}

        {pageState === 'already_cancelled' && booking && (
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <XCircle className="h-6 w-6 text-muted-foreground" />
              </div>
              <CardTitle>Redan avbokad</CardTitle>
              <CardDescription>
                Denna bokning är redan avbokad.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border bg-card p-4 space-y-3">
                <h3 className="font-semibold line-through text-muted-foreground">{booking.eventTitle}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CalendarDays className="h-4 w-4" />
                    <span>{formatDate(booking.eventDate)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>{booking.spots} platser</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Building2 className="h-4 w-4" />
                    <span>{booking.producerName}</span>
                  </div>
                </div>
              </div>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  Gå till startsidan
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {pageState === 'found' && booking && (
          <Card>
            <CardHeader className="text-center">
              <CardTitle>Avboka din plats</CardTitle>
              <CardDescription>
                Vill du avboka din bokning? Detta kan inte ångras.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border bg-card p-4 space-y-3">
                <h3 className="font-semibold text-primary">{booking.eventTitle}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CalendarDays className="h-4 w-4" />
                    <span>{formatDate(booking.eventDate)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>{booking.spots} platser bokade</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Building2 className="h-4 w-4" />
                    <span>{booking.producerName}</span>
                  </div>
                </div>
                {booking.guestName && (
                  <p className="text-sm text-muted-foreground pt-2 border-t">
                    Bokad av: {booking.guestName} ({booking.guestEmail})
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => setShowConfirmDialog(true)}
                >
                  Avboka bokningen
                </Button>
                <Link to="/">
                  <Button variant="outline" className="w-full">
                    Avbryt
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {pageState === 'cancelled' && booking && (
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <CardTitle>Bokningen är avbokad</CardTitle>
              <CardDescription>
                Din bokning har avbokats och en bekräftelse har skickats till din e-post.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border bg-card p-4 space-y-3">
                <h3 className="font-semibold line-through text-muted-foreground">{booking.eventTitle}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CalendarDays className="h-4 w-4" />
                    <span>{formatDate(booking.eventDate)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>{booking.spots} platser</span>
                  </div>
                </div>
              </div>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  Gå till startsidan
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bekräfta avbokning</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill avboka din plats på "{booking?.eventTitle}"? 
              Detta kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelling}>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelBooking}
              disabled={isCancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isCancelling ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Avbokar...
                </>
              ) : (
                'Ja, avboka'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
